import { z } from 'zod';

const MAX_PHOTO_BYTES = 5 * 1024 * 1024;
const ALLOWED_PHOTO_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];

// Client-side pre-check only, for instant feedback — the server is still
// the source of truth and returns its own 422 on `photo` if this slips
// through (e.g. a mislabeled mime type).
export const planPhotoSchema = z
  .instanceof(File)
  .optional()
  .refine((file) => !file || ALLOWED_PHOTO_TYPES.includes(file.type), 'Photo must be a JPG or PNG image.')
  .refine((file) => !file || file.size <= MAX_PHOTO_BYTES, 'Photo must be 5MB or smaller.');

// maximum_amount is optional — an empty string from the number input
// coerces to "leave it out" rather than 0, since 0 would fail the
// >= minimum_amount rule below on every plan that has a minimum > 0.
const optionalPositiveNumber = z.union([
  z.literal('').transform(() => undefined),
  z.coerce.number().positive('Must be greater than 0'),
]);

// v1.16.0 — payout structure, defaulting to 'monthly' to match the
// server's own default when the field is omitted on create.
export const PAYOUT_FREQUENCY_OPTIONS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'semi_annually', label: 'Semi-Annually' },
  { value: 'annually', label: 'Annually' },
  { value: 'at_maturity', label: 'At Maturity' },
];

export const investmentPlanSchema = z
  .object({
    name: z.string().min(2, 'Name is required'),
    description: z.string().min(1, 'Description is required'),
    minimum_amount: z.coerce.number().positive('Minimum amount must be greater than 0'),
    maximum_amount: optionalPositiveNumber.optional(),
    roi_percentage: z.coerce.number().min(0, 'ROI % must be 0 or more'),
    duration_in_days: z.coerce.number().int('Must be a whole number of days').positive('Must be greater than 0'),
    payout_frequency: z.enum(['monthly', 'quarterly', 'semi_annually', 'annually', 'at_maturity']).default('monthly'),
    photo: planPhotoSchema,
  })
  .refine((data) => data.maximum_amount === undefined || data.maximum_amount >= data.minimum_amount, {
    message: 'Maximum amount must be greater than or equal to the minimum',
    path: ['maximum_amount'],
  });
